// @flow

import _ from 'lodash'
import $ from 'jquery'
import v from 'validator'
import url from 'url'
import qs from 'querystring'
import moment from 'moment-timezone/builds/moment-timezone-with-data'
import { countryData } from '../services/apis'

function tryParse (data, fallback = null) {
  try {
    return JSON.parse(data)
  } catch (e) {
    return fallback
  }
}

type InputElement = HTMLInputElement & {
  checked: bool
}

type SelectElement = HTMLSelectElement & {
  selected: bool
}

interface FormOptions {
  el: HTMLFormElement,
  lib: Convrrt,
  recaptchaSiteKey: string,
  redirectUrl: string
}

interface FormFieldOptions {
  el: InputElement | SelectElement | HTMLTextAreaElement,
  form: Form,
}

interface Field {
  constructor (opts: FormFieldOptions): void,
  type (): string,
  name (): string,
  completed (): bool,
  value (): string | Array<mixed>,
}

const fieldTypes = {
  EMAIL: 'email',
  TEXT: 'text',
  DATE: 'date',
  URL: 'url',
  TEL: 'tel',
  DATETIME_LOCAL: 'datetime-local',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  NUMBER: 'number',
  HIDDEN: 'hidden',
  TEXTAREA: 'textarea',
  SELECT_ONE: 'select-one',
  SELECT_MULTIPLE: 'select-multiple',
  RECAPTCHA: 'g-recaptcha-response'
}

export class FormField implements Field {
  el: InputElement | SelectElement | HTMLTextAreaElement
  form: Form

  constructor (opts: FormFieldOptions) {
    this.el = opts.el
    this.el.addEventListener('input', this.validate.bind(this), true)
  }

  tags () {
    return []
  }

  validate () {
    if (this.value() && this.type() === 'url' && v.isURL(this.value(), {require_protocol: true}) === false) {
      this.el.setCustomValidity('Please add a valid URL with a protocol (http://)')
    } else {
      this.el.setCustomValidity('')
    }
  }

  fmt (val) {
    if (val && _.includes(['date', 'datetime-local'], this.type())) {
      return moment.tz(val, moment.tz.guess()).toISOString()
    }
    return val
  }

  type (): string {
    return this.el.type
  }

  value () {
    return this.fmt(this.el.value)
  }

  name (): string {
    return this.el.name
  }

  completed () {
    return !!this.value()
  }

  toJSON () {
    return {
      id: this.el.id,
      value: this.value(),
      tags: this.tags(),
      urlKey: this.el.dataset.urlKey,
      custom: this.el.dataset.custom === 'true',
      virtual: this.el.dataset.virtual === 'true'
    }
  }
}

export class InputField extends FormField {
}

export class SelectField extends FormField {
  selected () {
    return _.chain(this.el.querySelectorAll('option')).filter(o => o.selected)
  }

  tags () {
    return this.selected().map(o => tryParse(o.dataset.tagIds, [])).value()
  }

  value () {
    return this.selected().head().get('value').value()
  }
}

export class MultiSelectField extends SelectField {
  value () {
    return this.selected().map(o => o.value).value()
  }

  tags () {
    return this.selected().map(o => tryParse(o.dataset.tagIds, [])).value()
  }

  completed () {
    return this.value().length
  }
}

export class Checkbox extends FormField {
  value () {
    return this.el.checked
  }

  tags () {
    return tryParse(this.el.dataset.tagIds, [])
  }

  completed () {
    return this.value()
  }
}

export class Radio extends Checkbox {
  tags () {
    return this.value() ? tryParse(this.el.dataset.tagIds, []) : []
  }

  value () {
    return this.el.checked && this.el.value
  }
}

export function Init (opts: Object) {
  return [...document.querySelectorAll('form')].map((form) =>
    new Form({ el: form, ...opts })
  )
}

export default class Form {
  el: HTMLElement
  lib: Convrrt
  recaptchaContainer: HTMLElement | null
  redirectUrl: string | null
  recaptchaWidgetId: string
  fields: Array<FormField>
  globalTags: Array<string>

  constructor (opts: FormOptions) {
    this.el = opts.el
    this.lib = opts.lib
    this.redirectUrl = opts.redirectUrl
    this.fields = this.getFieldElements()
    this.recaptchaContainer = this.el.querySelector('.recaptcha-container')

    if (!opts.redirectUrl) {
      throw new Error(`form(${this.el.id}) is missing a redirectUrl`)
    }

    if (this.recaptchaContainer === null) {
      throw new Error(`form(${this.el.id}) is missing a .recaptcha-container`)
    }

    if (this.el.dataset.globalTagIds) {
      this.globalTags = JSON.parse(this.el.dataset.globalTagIds)
    }

    this.recaptchaWidgetId = opts.grecaptcha.render(this.recaptchaContainer, {
      size: 'invisible',
      sitekey: opts.recaptchaSiteKey,
      callback: this.recaptchaSuccess.bind(this)
    })

    this.el.addEventListener('submit', (e) => {
      e.preventDefault()

      this.validate()

      if (this.invalid()) return
      opts.grecaptcha.execute(this.recaptchaWidgetId)
    })

    this.bindCountrySelector()
  }

  validate () {
    this.fields.forEach(e => e.validate())
  }

  invalid () {
    return this.el.checkValidity() === false
  }

  recaptchaSuccess (token: string) {
    console.debug('recaptcha response', token)

    if (!token) {
      throw new Error('missing recaptcha response token')
    }

    return this.lib.apis.collect.form({
      ...this.toJSON(),
      [fieldTypes.RECAPTCHA]: token
    }).then(this.redirect.bind(this)).catch(this.handleError.bind(this))
  }

  redirect () {
    window.location.href = this.constructRedirectURL()
  }

  handleError (err: Error) {
    console.warn(err)
    alert('Sorry, we could not accept your submission at this time. Please try again.')
  }

  fieldFromElement (el: InputElement | SelectElement | HTMLTextAreaElement): Field {
    let type: string = el.type
    let form: Form = this
    let opts: FormFieldOptions = { el, form }

    switch (type) {
      case fieldTypes.SELECT_ONE:
        return new SelectField(opts)

      case fieldTypes.SELECT_MULTIPLE:
        return new MultiSelectField(opts)

      case fieldTypes.CHECKBOX:
        return new Checkbox(opts)

      case fieldTypes.RADIO:
        return new Radio(opts)

      default:
        return new InputField(opts)
    }
  }

  getFieldElements () {
    return [...this.el.querySelectorAll('input,textarea,select')].map(this.fieldFromElement)
  }

  formData () {
    return _
      .chain(this.getFieldElements(), f => f.name())
      .groupBy(f => f.name()).value()
  }

  serialize () {
    return qs.parse($(this.el).serialize())
  }

  redirectParams () {
    return _
      .chain(this.toJSON())
      .get('fields', [])
      .filter(f => isNaN(+f.id))
      .filter(f => f.value)
      .map(f => [f.urlKey || f.id, f.value])
      .fromPairs()
      .value()
  }

  constructRedirectURL () {
    let redir = url.parse(this.redirectUrl || '')
    let params = qs.parse(redir.query || '')
    let redirParams = this.redirectParams()
    let search = qs.stringify({
      ...params,
      ...redirParams
    })

    return url.format({
      ...redir,
      search
    })
  }

  toJSON () {
    let grouped = {
      addresses: []
    }

    let formData = this.formData()

    for (let key in formData) {
      _.set(grouped, key, formData[key])
    }

    grouped.fields = _.chain(grouped.fields).keys().map(k => {
      return _.chain(grouped.fields[k]).filter(f => f.completed()).head().value()
    }).filter(f => f !== undefined).map(f => f.toJSON()).value()

    grouped.meta = _.mapValues(grouped.meta, f => _.chain(f).head().get('el.value').value())

    grouped.tags = _.flatten([
      ...this.globalTags,
      ..._.chain(grouped.fields).map('tags').flatten().value()
    ])

    let addresses = _.pick(grouped, 'billing_address', 'shipping_address', 'other_address')

    _.forEach(addresses, addr => {
      grouped.addresses.push(_.mapValues(addr, f => _.chain(f).head().get('el.value').value()))
    })

    delete grouped.billing_address
    delete grouped.shipping_address
    delete grouped.other_address

    return grouped
  }

  bindCountrySelector () {
    let form = $(this.el)
    let addressFields = ['billing_address', 'shipping_address', 'other_address']

    addressFields.forEach((f) => {
      let countrySelector = form.find(`[name="${f}[country_code]"]`)
      let regionSelector = form.find(`[name="${f}[region]"]`)

      countrySelector.on('change', function () {
        let selected = $(this).find(':selected').get(0)
        regionSelector.empty()
        regionSelector.prop('disabled', true)
        regionSelector.addClass('disabled')

        countryData.regions(selected.value).then((states) => {
          states = _.sortBy(states, ['name', 'desc'])
          regionSelector.prop('disabled', false)
          regionSelector.removeClass('disabled')

          // inject first empty option
          states.unshift({
            name: '-- select a state / region --',
            alpha3: '',
            categoryId: '',
            categoryName: '',
            countryAlpha2: '',
            countryAlpha3: ''
          })

          regionSelector.empty().append(states.map(o => $(`<option value="${o.alpha3}">${o.name}</option>`)))
        })
      })
    })
  }
}
